import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { hasActionAccess } from '../../../utils/roleBasedAccess';

const BulkActionsDropdown = ({ selectedCount, onBulkAction, disabled = false, userRole }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const bulkActions = [
    {
      id: 'move-to-interview',
      label: 'Move to Interview',
      icon: 'Calendar',
      description: 'Schedule interviews for selected candidates',
      color: 'text-blue-600',
      requiredAccess: { type: 'interviews', action: 'schedule' }
    },
    {
      id: 'update-status',
      label: 'Update Status',
      icon: 'Edit',
      description: 'Change status for selected candidates',
      color: 'text-purple-600',
      requiredAccess: { type: 'candidateActions', action: 'edit' }
    },
    {
      id: 'send-email',
      label: 'Send Email',
      icon: 'Mail',
      description: 'Send bulk email to selected candidates',
      color: 'text-green-600',
      requiredAccess: { type: 'candidateActions', action: 'edit' }
    },
    {
      id: 'add-tags',
      label: 'Add Tags',
      icon: 'Tag',
      description: 'Add tags to selected candidates',
      color: 'text-orange-600',
      requiredAccess: { type: 'candidateActions', action: 'edit' }
    },
    {
      id: 'export-data',
      label: 'Export Data',
      icon: 'Download',
      description: 'Export selected candidate data',
      color: 'text-indigo-600',
      requiredAccess: { type: 'candidateActions', action: 'export' }
    },
    {
      id: 'archive',
      label: 'Archive',
      icon: 'Archive',
      description: 'Archive selected candidates',
      color: 'text-gray-600',
      requiredAccess: { type: 'candidateActions', action: 'edit' }
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Trash2',
      description: 'Permanently delete selected candidates',
      color: 'text-red-600',
      dangerous: true,
      requiredAccess: { type: 'candidateActions', action: 'delete' }
    }
  ];

  // Filter actions based on user role
  const availableActions = bulkActions.filter(action => {
    if (!action.requiredAccess) return true;
    return hasActionAccess(userRole, action.requiredAccess.type, action.requiredAccess.action);
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleActionClick = (action) => {
    onBulkAction(action?.id);
    setIsOpen(false);
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        iconName="ChevronDown"
        iconPosition="right"
        iconSize={16}
      >
        Bulk Actions ({selectedCount})
      </Button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-popover border border-border rounded-lg shadow-elevation-modal z-50">
          <div className="p-3 border-b border-border">
            <h3 className="font-medium text-popover-foreground">
              Bulk Actions
            </h3>
            <p className="text-sm text-muted-foreground">
              {selectedCount} candidate{selectedCount > 1 ? 's' : ''} selected
            </p>
          </div>
          
          <div className="py-2 max-h-80 overflow-y-auto">
            {availableActions?.map((action) => (
              <button
                key={action?.id}
                onClick={() => handleActionClick(action)}
                className={`w-full flex items-start space-x-3 px-3 py-3 text-left hover:bg-muted transition-colors ${
                  action?.dangerous ? 'hover:bg-destructive/10' : ''
                }`}
              >
                <Icon 
                  name={action?.icon} 
                  size={18} 
                  className={`mt-0.5 flex-shrink-0 ${action?.color}`}
                />
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${action?.dangerous ? 'text-destructive' : 'text-popover-foreground'}`}>
                    {action?.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {action?.description}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="p-3 border-t border-border bg-muted/30">
            <div className="flex items-center text-xs text-muted-foreground">
              <Icon name="Info" size={12} className="mr-1" />
              <span>Actions will be applied to all selected candidates</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActionsDropdown;